import React, { createContext, useContext, useEffect } from 'react';
import { theme } from '../theme/theme';

interface ThemeContextType {
  theme: typeof theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply theme colors as CSS variables
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-primary-dark', theme.colors.primaryDark);
    root.style.setProperty('--color-primary-light', theme.colors.primaryLight);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-secondary-dark', theme.colors.secondaryDark);
    root.style.setProperty('--color-secondary-light', theme.colors.secondaryLight);
    root.style.setProperty('--color-orange', theme.colors.secondary);
    root.style.setProperty('--color-orange-dark', theme.colors.secondaryDark);
    root.style.setProperty('--text-color', theme.colors.text);
    root.style.setProperty('--text-secondary', theme.colors.textSecondary);
    root.style.setProperty('--muted-text', theme.colors.textMuted);
    root.style.setProperty('--text-light', theme.colors.textLight);
    root.style.setProperty('--bg', theme.colors.background);
    root.style.setProperty('--surface', theme.colors.surface);
    root.style.setProperty('--card', theme.colors.card);
    root.style.setProperty('--border', theme.colors.border);
    root.style.setProperty('--input', theme.colors.input);
    root.style.setProperty('--error', theme.colors.error);
    root.style.setProperty('--success', theme.colors.success);
    root.style.setProperty('--warning', theme.colors.warning);
    root.style.setProperty('--info', theme.colors.info);
    
    // Apply spacing
    root.style.setProperty('--spacing-xs', `${theme.spacing.xs}px`);
    root.style.setProperty('--spacing-sm', `${theme.spacing.sm}px`);
    root.style.setProperty('--spacing-md', `${theme.spacing.md}px`);
    root.style.setProperty('--spacing-lg', `${theme.spacing.lg}px`);
    root.style.setProperty('--spacing-xl', `${theme.spacing.xl}px`);
    root.style.setProperty('--spacing-xxl', `${theme.spacing.xxl}px`);
    
    // Apply border radius
    root.style.setProperty('--radius-sm', `${theme.borderRadius.sm}px`);
    root.style.setProperty('--radius-md', `${theme.borderRadius.md}px`);
    root.style.setProperty('--radius-lg', `${theme.borderRadius.lg}px`);
    root.style.setProperty('--radius-xl', `${theme.borderRadius.xl}px`);
    root.style.setProperty('--radius-2xl', `${theme.borderRadius['2xl']}px`);
    
    // Apply shadows
    root.style.setProperty('--shadow-soft', theme.shadows.soft);
    root.style.setProperty('--shadow-card', theme.shadows.card);
    root.style.setProperty('--shadow-button', theme.shadows.button);
    root.style.setProperty('--shadow-medium', theme.shadows.medium);
    root.style.setProperty('--shadow-elevated', theme.shadows.elevated);
    
    // Apply typography
    root.style.setProperty('--h1-size', `${theme.typography.h1}px`);
    root.style.setProperty('--h2-size', `${theme.typography.h2}px`);
    root.style.setProperty('--h3-size', `${theme.typography.h3}px`);
    root.style.setProperty('--h4-size', `${theme.typography.h4}px`);
    root.style.setProperty('--body-size', `${theme.typography.body}px`);
    root.style.setProperty('--body-small', `${theme.typography.bodySmall}px`);
    root.style.setProperty('--caption', `${theme.typography.caption}px`);
    root.style.setProperty('--button-size', `${theme.typography.button}px`);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme }}>
      {children}
    </ThemeContext.Provider>
  );
};









